/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useMemo, useState } from 'react';
import { PieChart } from 'react-minimal-pie-chart';
import { useTheme } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';
import makeStyles from '@material-ui/styles/makeStyles';
import { SubmissionStatus } from '../../../models/SubmissionStatus';
import useProcedures from '../../../procedure/useProcedures';

const dotSize = '0.9rem';
const useStyles = makeStyles(() => ({
  tooltip: {
    '&.show': {
      opacity: '1 !important',
    },
  },
  dot: {
    height: dotSize,
    width: dotSize,
    borderRadius: '50%',
    display: 'inline-block',
  },
  tooltipTitle: {
    fontSize: '1rem',
    marginLeft: '0.3rem',
  },
  tooltipDescription: {
    marginTop: '0.5rem',
    marginLeft: `calc(0.3rem + ${dotSize})`,
    fontSize: '0.8rem',
  },
}));

export function DashboardCardChart({ data, total, label }) {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(null);
  const { getProcedure } = useProcedures();

  const chartData = useMemo(() => {
    if (total <= 0) {
      return [
        {
          title: t('dashboard.empty'),
          value: 100,
          color: theme.palette.emptyChart,
          tooltip: t('dashboard.empty'),
        },
      ];
    }
    return data.reduce((accumulator, item) => {
      accumulator.push({
        title: t(SubmissionStatus[item?.statusName]?.i18nKey),
        value: item.total,
        color: theme.palette.statusChip[item?.statusName]?.main,
        countByProcedure: item.countByProcedure,
      });
      return accumulator;
    }, []);
  }, [data, total, t, theme]);

  const makeTooltipContent = entry => (
    <div>
      <div>
        <span className={classes.dot} style={{ background: theme.palette.statusChip[entry?.statusName]?.main }} />
        <span className={classes.tooltipTitle}>{t(SubmissionStatus[entry?.statusName]?.i18nKey)}</span>
      </div>
      {entry?.countByProcedure && (
        <div className={classes.tooltipDescription}>
          {Object.entries(entry?.countByProcedure).map(([key, value]) => (
            <div>{`${t(getProcedure(key)?.label ?? key)}: ${value}`}</div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div data-tip="" data-for={label} aria-describedby={label}>
      <PieChart
        data={chartData}
        radius={PieChart.defaultProps.radius - 10}
        lineWidth={PieChart.defaultProps.lineWidth - 60}
        labelPosition={total > 0 ? PieChart.defaultProps.labelPosition : 0}
        label={({ dataEntry }) => (total > 0 ? '' : dataEntry.title)}
        labelStyle={{
          fontSize: '.5rem',
          fontFamily: 'inherit',
          fill: theme.palette.input,
        }}
        onMouseOver={(_, index) => {
          setHovered(index);
        }}
        onMouseOut={() => {
          setHovered(null);
        }}
      />

      <ReactTooltip
        id={label}
        backgroundColor={theme.palette.tooltipBackground}
        borderColor={theme.palette.tooltipBackground}
        arrowColor={theme.palette.tooltipBackground}
        className={classes.tooltip}
        getContent={() => (typeof hovered === 'number' && data[hovered] ? makeTooltipContent(data[hovered]) : null)}
      />
    </div>
  );
}
